import { useContext } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Utils, ThemeContext } from '@gv/triage-components';
import { Box, Slide, Avatar, Snackbar, IconButton } from '@mui/material';

import { useAppSelector } from 'store';
import { selectAuthForGVTalk } from 'store/slices/auth';
import { useLazyDetailsQuery } from 'store/api/gv-chats';
import { RealTimeChatContext } from 'context/real-time-chat';

import * as Styles from './styles';

const { parseContact, getChatUserName } = Utils.Helpers;

function SlideTransition(props: any) {
  return <Slide {...props} direction="down" />;
}

// Add avatar fallback if icon is not provided
const AvatarOrIcon = ({
  icon,
  children,
}: {
  icon?: string;
  children: React.ReactNode;
}) =>
  icon ? (
    <Avatar src={icon} sx={{ width: 40, height: 40 }} />
  ) : (
    <Styles.AvatarBox>{children}</Styles.AvatarBox>
  );

export interface AlertMessageProps {
  body: string;
  icon?: string;
  index?: number;
  chatId?: string;
  senderName?: string;
  senderRole?: string;
  showAlertMessage: boolean;
  setShowAlertMessage: (show: boolean) => void;
}

export const AlertMessage = ({
  body,
  icon,
  chatId,
  index = 0,
  senderRole,
  senderName,
  showAlertMessage,
  setShowAlertMessage,
}: AlertMessageProps): JSX.Element => {
  const { isDarkTheme } = useContext(ThemeContext);

  // Safely get RealTimeChat context - it might not be available if rendered outside provider
  // Using useContext directly instead of the hook to avoid throwing errors
  const realTimeChatContext = useContext(RealTimeChatContext);

  const gvTalkAuth = useAppSelector(selectAuthForGVTalk);
  const [fetchChatDetails] = useLazyDetailsQuery();

  // Prevent closing on clickaway
  const onClose = (event?: Event | React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowAlertMessage(false);
  };

  // Handle opening chat sidebar when user clicks reply
  const handleReply = async () => {
    if (!chatId) {
      console.warn('No chatId provided, cannot open chat sidebar');
      setShowAlertMessage(false);
      return;
    }

    if (!realTimeChatContext) {
      console.error(
        'RealTimeChat context is not available. Make sure RealTimeChatProvider is wrapping the app.'
      );
      setShowAlertMessage(false);
      return;
    }

    const { setIsChatOpen, setSelectedChat } = realTimeChatContext;
    console.log('Opening chat sidebar for chatId:', chatId);

    try {
      // Fetch chat details to get member information
      const result = await fetchChatDetails({ id: Number(chatId) });
      const chatDetails = result.data;

      if (!chatDetails) {
        return;
      }

      // Find the other member (not the current user)
      const { authUserId, authClientId } = gvTalkAuth;
      const otherMember = chatDetails.members?.find((member) => {
        if (authUserId) {
          return member.userId !== authUserId;
        } else if (authClientId) {
          return member.clientId !== authClientId;
        }
        return true;
      });

      if (otherMember) {
        const contact = parseContact(otherMember);
        const memberPic =
          (otherMember as any).profile_pic ||
          (otherMember as any).avatar ||
          undefined;
        setSelectedChat({
          userId: otherMember.id,
          chatId: Number(chatId),
          userImageUrl: memberPic != null ? String(memberPic) : icon,
          userName: (getChatUserName(contact ?? {}) ?? senderName) as string,
          userRole: contact?.role != null ? String(contact.role) : senderRole,
        });
        // If no other member found (group chat), use chat name
        const chatProps = Utils.GVTalk.getChatCardProps(
          chatDetails,
          gvTalkAuth
        );
        setSelectedChat({
          userId: 0,
          userImageUrl: icon,
          userRole: senderRole,
          chatId: Number(chatId),
          userName: chatProps.chat.title ?? senderName ?? '',
        });
      }

      // Open the chat sidebar
      console.log('Setting selected chat and opening sidebar');
      setIsChatOpen(true);
      // Close the notification popup
      setShowAlertMessage(false);
      console.log('Chat sidebar should now be open');
    } catch (error) {
      console.error('Error opening chat sidebar:', error);
      // Still close the popup even if there's an error
      setShowAlertMessage(false);
    }
  };

  // Calculate offset for stacking notifications (each notification is offset by its index)
  // Each notification card is approximately 200px tall, so we offset by index * 220px
  const topOffset = index * 220;

  return (
    <Snackbar
      onClose={onClose}
      open={showAlertMessage}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        zIndex: 1400 + index, // Higher z-index for newer notifications
        '&.MuiSnackbar-root': {
          bottom: 'auto !important',
          top: `${24 + topOffset}px !important`,
        },
      }}
    >
      <Styles.StyledCard isDarkTheme={isDarkTheme}>
        <Styles.HeaderRow>
          <Styles.InfoRow>
            <AvatarOrIcon icon={icon}>CS</AvatarOrIcon>
            <Box>
              <Styles.NameText isDarkTheme={isDarkTheme}>
                {senderName}
              </Styles.NameText>
              <Styles.RoleText isDarkTheme={isDarkTheme}>
                {senderRole}
              </Styles.RoleText>
            </Box>
          </Styles.InfoRow>
          <IconButton size="small" onClick={onClose} sx={{ color: '#999' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Styles.HeaderRow>

        <Styles.MessageText isDarkTheme={isDarkTheme}>
          {body}
        </Styles.MessageText>

        {chatId && (
          <Box
            onClick={handleReply}
            sx={{
              marginTop: 1,
              opacity: 0.5,
              cursor: 'pointer',
              fontSize: '0.95rem',
              alignSelf: 'flex-start',
              color: isDarkTheme ? '#ccc' : '#666',
            }}
          >
            Click here to reply...
          </Box>
        )}
      </Styles.StyledCard>
    </Snackbar>
  );
};
